import type React from "react";
import { useEffect, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import noop from "lodash/noop";

import { colorsPalettes } from "@similarweb/styles";

import { ALL } from "app/common/services/categoryService";
import useActivationQueryWithAccessControl from "app/custom-hooks/activation/useActivationQueryWithAccessControl";
import type { ShortActivationSetSchema } from "app/pages/activation/types/api";
import { useCommonProps } from "app/pages/keyword-research/keyword-analysis/pages/serp-players/useCommonProps";
import { getShareValue, getVolumeValue } from "app/pages/website-analysis/traffic-sources/search/common/utils";
import { keywordGroupsQueryClient, keywordGroupsTransforms } from "app/services/keyword-groups";
import { createUniqueListOfAllDomains } from "pro-features/components/DomainsFilter/Utils";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { Graph } from "../../common-components/clicks-over-time-graph/Graph";
import { DomainsTable, EColumnActions } from "../../common-components/domains-table/Table";
import { useKeywordAnalysisNavigationTargets } from "app/pages/keyword-research/keyword-analysis/common-components/keywordAnalysisNavigationTargets";
import { FiltersSection } from "../serp-players/FiltersSection/FiltersSection";
import { MetricsRow } from "../serp-players/styledComponents";

import type { IData, IEnrichData, IEnrichTransformResult, UrlTableTransformResult } from "../serp-players/types";

import {
    dropDownColumnsTexts,
    entity,
    GRAPH_DASHBOARD_METRIC,
    graphTitles,
    SourceTypeMap,
    TABLE_DASHBOARD_METRIC,
    visibleColumns,
    visibleColumnsEnrich,
} from "./consts";
import { usePagesEndpoints } from "./pagesEndpoints";

export const Pages: React.FC = () => {
    const { params } = useCurrentStateAndParams();
    const endpoints = usePagesEndpoints();
    const navTargets = useKeywordAnalysisNavigationTargets();
    const commonProps = useCommonProps(true);
    const { category, funcFlag, domains } = params;

    const isKeywordGroup = keywordGroupsTransforms.isKeywordGroup(params.keyword);
    const {
        data: activationSetsData,
        isLoading: loadingActivationSet,
        isFetching: fetchingActivationSet,
    } = useActivationQueryWithAccessControl();

    const [activationSets, setActivationSets] = useState<ShortActivationSetSchema[]>([]);

    useEffect(() => {
        if (!loadingActivationSet && !fetchingActivationSet) {
            setActivationSets(activationSetsData);
        }
    }, [loadingActivationSet, fetchingActivationSet]);

    const transformDataEnrich = (data: IEnrichData): IEnrichTransformResult => {
        return {
            ...data,
            records: data.Data.map((record) => {
                const shareOfOrganicClicks = record.OrganicClicks / record.OriginalClicks;
                return {
                    monthlyVolume: getVolumeValue(record.Volume),
                    windowVolume: getVolumeValue(record.Volume),
                    OrganicClicksShare: shareOfOrganicClicks,
                    Intent: {
                        PrimaryIntent: record.PrimaryIntent,
                        SecondaryIntent: record.SecondaryIntent,
                    },
                    ...record,
                };
            }),
            TotalCount: data.TotalCount,
        };
    };

    const transformData = async (data: IData): Promise<UrlTableTransformResult> => {
        return {
            ...data,
            records: await Promise.all(
                (data?.Data ?? []).map(async (record) => {
                    const shareOfDesktop = record.DesktopClicks / record.OriginalClicks;
                    const keywordNameForSERP = isKeywordGroup
                        ? (await keywordGroupsQueryClient.getGroupById(params.keyword))?.Name
                        : params.keyword;
                    const organicClicksShare = record.OrganicClicks / record.OriginalClicks;

                    return {
                        ...record,
                        urls: [
                            {
                                name: record.Domain,
                                dropdownItems: [
                                    {
                                        name: EColumnActions[EColumnActions.Explore],
                                        id: record.Domain,
                                        iconName: "search-keywords",
                                        iconSize: "sm",
                                        text: dropDownColumnsTexts.exploreUrlKeywords,
                                        iconColor: colorsPalettes.carbon[200],
                                    },
                                    {
                                        name: EColumnActions[EColumnActions.Copy],
                                        id: record.Domain,
                                        iconName: "organic-site-links",
                                        iconSize: "sm",
                                        text: dropDownColumnsTexts.copyUrlTitle,
                                        iconColor: colorsPalettes.carbon[200],
                                    },
                                    {
                                        name: EColumnActions[EColumnActions.Open],
                                        id: record.Domain,
                                        iconName: "link-out",
                                        iconSize: "sm",
                                        text: dropDownColumnsTexts.openUrlTitle,
                                        iconColor: colorsPalettes.carbon[200],
                                    },
                                ],
                            },
                        ],
                        Share: getShareValue(record.Share),
                        Clicks: getVolumeValue(record.Clicks),
                        selectable: true,
                        href: proNavigator.href(navTargets.websiteKeywordsState, {
                            ...params,
                            key: record.Domain,
                            isWWW: "*",
                        }),
                        WebSourceShare: shareOfDesktop,
                        SearchTerm: keywordNameForSERP,
                        SiteSerpFeatures: { [record.Domain]: record.SerpFeatures },
                        SerpScrapeDate: record.LatestKeywordSerpDate,
                        OrganicClicksShare: organicClicksShare,
                        totalMonthlyVolume: getVolumeValue(record.Volume),
                        totalWindowVolume: getVolumeValue(record.Volume),
                        AvgPos: record.Position,
                        AvgCompetition: record.Competition,
                        AvgDifficulty: record.Difficulty,
                        TopIntent: {
                            PrimaryIntent: record.PrimaryIntent,
                            SecondaryIntent: record.SecondaryIntent,
                        },
                    };
                }),
            ),
        };
    };

    return (
        <>
            <FiltersSection
                domainsAPI={endpoints.domains}
                categoriesAPI={endpoints.categories}
                activationSets={activationSets}
            />
            {activationSets ? (
                <>
                    <MetricsRow>
                        <Graph
                            isKeywordsGroup={commonProps.isKeywordsGroup}
                            filters={{
                                category: category === ALL ? undefined : category,
                                funcFlag,
                                DomainsFilter: createUniqueListOfAllDomains(domains?.split(","), activationSets),
                                sourceType: SourceTypeMap[params.selectedPageTab],
                            }}
                            endpoint={endpoints.graph}
                            displayTooltipOnLegendOver={true}
                            dashboardMetric={GRAPH_DASHBOARD_METRIC}
                            graphTitles={graphTitles}
                            excelEndpoint={endpoints.graphExcel}
                        />
                    </MetricsRow>
                    <MetricsRow>
                        <DomainsTable
                            getDataCallback={noop}
                            tableSelectionKey={"keywordAnalysis.mobile.v2.Table"}
                            addToDashboardMetric="KeywordAnalysisTotal"
                            isKeywordsGroup={commonProps.isKeywordsGroup}
                            tableEndpoint={endpoints.table}
                            enrichTableEndpoint={endpoints.enrichTable}
                            visibleColumns={visibleColumns(isKeywordGroup)}
                            visibleColumnsEnrich={visibleColumnsEnrich}
                            dropDownColumnsTexts={dropDownColumnsTexts}
                            transformData={transformData}
                            transformDataEnrich={transformDataEnrich}
                            tableHeaderTooltip={"mobile.keywords.2.table.url.title.tooltip"}
                            removeLinkFromEnrichTable={true}
                            entity={entity}
                            dashboardMetric={TABLE_DASHBOARD_METRIC}
                            activationSets={activationSets}
                            websiteKeywordsState={navTargets.websiteKeywordsState}
                            keywordAdsState={navTargets.keywordAdsState}
                            keywordSerpPlayersState={navTargets.keywordSerpPlayersState}
                        />
                    </MetricsRow>
                </>
            ) : null}
        </>
    );
};
